// Kurzlinks: kurze, lesbare Adressen für Stellen, an denen niemand klicken kann (Text in einem Bild),
// die beim Aufruf auf die Zielseite mit UTM umschreiben. So bleiben geteilte Bilder messbar.
//
// Warum vor React (main.tsx) und nicht als <Navigate> im Router: Die Tracker (useUtmCapture,
// first_touch.landing_page, Sitzungsstart, GA) lesen window.location in Effekten, die VOR dem
// Effekt einer Weiterleitungs-Route laufen. Eine Router-Weiterleitung käme zu spät, die UTM wäre
// verloren. history.replaceState vor dem ersten Render dagegen sieht jeder Tracker. Kein Reload,
// kein Server, keine neue Infrastruktur.
//
// Abhängigkeitsfrei halten: Die Datei liegt im Einstieg jeder Seite.

// Teilen v1 (T1, Scope 03.11.2026, E1): persönlicher Teilen-Link charliescrew.com/r/CODE in
// Sticker-, Rückblick- und Foto-Bildern. Schreibt um auf
//   /schnuppertag?ref=CODE&utm_source=app&utm_medium=referral&utm_campaign=…&utm_content=…
// Anlass und Format stehen optional als Pfad dahinter (/r/CODE/sticker/chat); im Bild steht nur
// /r/CODE (abtippbar), dort ist utm_campaign „bild“. Ungültige Codes landen ohne ref auf dem
// Schnuppertag. Der Code ist nur ein Pseudonym des Werbers, auf der Seite steht nichts über ihn.

/** Pfad-Präfix des persönlichen Teilen-Links. */
export const REF_KURZLINK_PRAEFIX = "/r/";
const REF_LINK_MUSTER = /^[A-Z]{2}[A-Z0-9]{4,5}$/;
const SEGMENT_MUSTER = /^[a-z0-9_]{1,24}$/;

/** „charliescrew.com/r/MA1234“ (so steht der Link im Bild). */
export function refLinkText(code: string): string {
  return `charliescrew.com${REF_KURZLINK_PRAEFIX}${code.toUpperCase()}`;
}

/**
 * Klickbarer Link für Text (Empfehlung per WhatsApp/Mail/Teilen): direkt auf den Schnuppertag mit
 * UTM, damit die Link-Vorschau den Schnuppertag zeigt und kein Umweg entsteht.
 * www. ist Pflicht: charliescrew.com antwortet mit 302 auf www.charliescrew.com.
 */
export function refLinkUrl(code: string, anlass: string, format: string): string {
  const p = new URLSearchParams({ ref: code.toUpperCase(), utm_source: "app", utm_medium: "referral", utm_campaign: anlass, utm_content: format });
  return `https://www.charliescrew.com/schnuppertag?${p.toString()}`;
}

/** Pfad im Teilen-Bild des Monatsrückblicks („charliescrew.com/rudel“), bis Teilen v1. Bleibt als Weiterleitung. */
export const RECAP_KURZLINK_PFAD = "/rudel";
/** So steht der Link im Bild. */
export const RECAP_KURZLINK_TEXT = `charliescrew.com${RECAP_KURZLINK_PFAD}`;

/** Vormonat in Berlin als „recap_YYYY_MM“: geteilt wird fast nur in den Tagen nach dem Monatsersten. */
export function recapKampagne(jetzt: Date): string {
  const teile = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Berlin", year: "numeric", month: "2-digit" }).formatToParts(jetzt);
  let jahr = Number(teile.find((t) => t.type === "year")?.value);
  let monat = Number(teile.find((t) => t.type === "month")?.value) - 1;
  if (monat === 0) {
    monat = 12;
    jahr -= 1;
  }
  return `recap_${jahr}_${String(monat).padStart(2, "0")}`;
}

/**
 * Ziel-Adresse (Pfad + Query) für einen Kurzlink oder null, wenn der Pfad keiner ist.
 * Groß/klein und ein Schrägstrich am Ende sind egal; vorhandene Parameter bleiben erhalten,
 * eigene utm_* im Aufruf gewinnen (z. B. wenn jemand den Link mit UTM weitergibt).
 */
export function kurzlinkZiel(pathname: string, search: string, jetzt: Date): string | null {
  const roh = pathname.replace(/\/+$/, "");
  const pfad = roh.toLowerCase();
  const params = new URLSearchParams(search);
  const setze = (utm: Record<string, string>) => {
    for (const [k, v] of Object.entries(utm)) if (!params.has(k)) params.set(k, v);
  };

  // Persönlicher Teilen-Link /r/CODE[/anlass[/format]]
  if (pfad.startsWith(REF_KURZLINK_PRAEFIX)) {
    const [codeRoh = "", anlassRoh = "", formatRoh = ""] = roh.slice(REF_KURZLINK_PRAEFIX.length).split("/");
    const code = decodeURIComponent(codeRoh).trim().toUpperCase();
    const anlass = anlassRoh.toLowerCase();
    const format = formatRoh.toLowerCase();
    if (REF_LINK_MUSTER.test(code) && !params.has("ref")) params.set("ref", code);
    setze({
      utm_source: "app",
      utm_medium: "referral",
      utm_campaign: SEGMENT_MUSTER.test(anlass) ? anlass : "bild",
      utm_content: SEGMENT_MUSTER.test(format) ? format : "bild",
    });
    return `/schnuppertag?${params.toString()}`;
  }

  // Alter Rückblick-Link (bis Teilen v1 im Bild): Startseite, jetzt im UTM-Vokabular von E1.
  if (pfad !== RECAP_KURZLINK_PFAD) return null;
  setze({ utm_source: "app", utm_medium: "referral", utm_campaign: recapKampagne(jetzt), utm_content: "rudel_link" });
  return `/?${params.toString()}`;
}

/** Vor dem ersten Render aufrufen (main.tsx). Darf nie werfen. */
export function kurzlinkAnwenden(): void {
  try {
    const ziel = kurzlinkZiel(window.location.pathname, window.location.search, new Date());
    if (ziel) window.history.replaceState(window.history.state, "", ziel + window.location.hash);
  } catch {
    /* Ein Kurzlink darf die Seite nie brechen; schlimmstenfalls landet man auf der 404. */
  }
}
